import { zodResolver } from "@hookform/resolvers/zod"
import { useTranslations } from "next-intl"
import { useForm } from "react-hook-form"

import { createCustomerProfileAction, updateCustomerProfileAction } from "@/app/[locale]/customer/actions"
import FormFieldError from "@/components/shared/FormFieldError"
import { useServerAction } from "@/hooks/useServerAction"
import type { CustomerProfileSchema } from "@/schemas/Customer/CustomerProfileSchemas"
import { CustomerProfileSchemas } from "@/schemas/Customer/CustomerProfileSchemas"

import { BirthdatePicker } from "../shared/BirthDatePicker"
import { Button } from "../ui/button"
import { Label } from "../ui/label"
import type { CustomerProfile } from "@/lib/zenstack/models"
import { ClientUtils } from "@/utils/client/ClientUtils"
import { Input } from "../ui/input"
import { Link } from "@/i18n/routing"
import { ROUTES } from "@/routes"

export default function CustomerProfile({ customerProfile }: { customerProfile?: CustomerProfile }) {
  const t = useTranslations("customer")
  const tCommon = useTranslations("common")
  const { serverAction: createCustomerProfile, isPending: isCreating } = useServerAction(createCustomerProfileAction, {
    showToast: true,
  })
  const { serverAction: updateCustomerProfile, isPending: isUpdating } = useServerAction(updateCustomerProfileAction, {
    showToast: true,
  })

  const {
    register,
    formState: { errors, dirtyFields, isValid },
    handleSubmit,
    getValues,
    setValue,
  } = useForm<CustomerProfileSchema>({
    resolver: zodResolver(customerProfile ? CustomerProfileSchemas.update(t) : CustomerProfileSchemas.create(t)),
    defaultValues: {
      birthDate: new Date(customerProfile?.birthdate ?? new Date()).toISOString(),
      fullName: customerProfile?.fullName,
    },
  })

  const onSubmit = (data: CustomerProfileSchema) => {
    if (customerProfile) {
      const dirtyData = ClientUtils.dirtyValues(dirtyFields, data)
      updateCustomerProfile({ customerId: customerProfile.id, data: dirtyData as any })
    } else {
      createCustomerProfile(data)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex max-w-md flex-col gap-4">
      <div>
        <Label htmlFor="fullName">{t("profile.form.fullName")}</Label>
        <Input {...register("fullName")} placeholder={t("profile.form.fullNamePlaceholder")} />
        <FormFieldError error={errors.fullName?.message} />
      </div>
      <div>
        <Label htmlFor="birthDate">{t("profile.form.dateOfBirth")}</Label>
        <BirthdatePicker
          isProfileUpdate={!!customerProfile}
          value={new Date(getValues("birthDate"))}
          onDateChange={(date) => {
            setValue("birthDate", date.toISOString(), { shouldDirty: true, shouldValidate: true })
          }}
        />
        <p className="ml-[6px] mt-[4px] text-xs text-muted-foreground">{t("profile.form.ageRestriction")}</p>
        <FormFieldError error={errors.birthDate?.message} />
      </div>
      <Button type="submit" className="w-full" disabled={isCreating || isUpdating || !isValid}>
        {customerProfile ? tCommon("actions.save") : t("welcome.button")}
      </Button>
      {customerProfile && (
        <div className="flex flex-col items-center">
          <Link href={ROUTES.updateEmail}>
            <Button variant="link" className="underline">
              {tCommon("auth.email.title")}
            </Button>
          </Link>
          <Link href={ROUTES.updatePassword}>
            <Button variant="link" className="underline">
              {tCommon("auth.password.title")}
            </Button>
          </Link>
        </div>
      )}
    </form>
  )
}
